import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
export const authConfigured = Boolean(url && key);
export const supabase: SupabaseClient | null = authConfigured ? createClient(url!, key!, {
  auth: { flowType: 'pkce', persistSession: true, autoRefreshToken: true, detectSessionInUrl: false }
}) : null;

let authCallbackPromise: Promise<Session> | null = null;

export async function signInWithGoogle(returnTo = window.location.pathname) {
  if (!supabase) throw new Error('Logowanie nie jest jeszcze skonfigurowane.');
  authCallbackPromise = null;
  sessionStorage.setItem('auth:returnTo', returnTo);
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin + '/auth/callback', scopes: 'openid email profile' }
  });
  if (error) throw error;
}

export function authErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (/pkce code verifier/i.test(message)) {
    return 'Sesja logowania wygasła albo callback został otwarty ponownie. Rozpocznij logowanie jeszcze raz w tej samej karcie.';
  }
  return message || 'Nie udało się zakończyć logowania.';
}

export async function finishAuthCallback() {
  if (!supabase) throw new Error('Brak konfiguracji Supabase.');
  if (authCallbackPromise) return authCallbackPromise;
  const callback = async () => {
    const parameters = new URLSearchParams(window.location.search);
    const callbackError = parameters.get('error_description') || parameters.get('error');
    if (callbackError) throw new Error(callbackError);
    const code = parameters.get('code');
    if (code) {
      // A new callback can select account B while account A still has a session.
      // Never complete B's registration using A's existing token.
      const exchanged = await supabase.auth.exchangeCodeForSession(code);
      if (exchanged.error) throw exchanged.error;
      if (!exchanged.data.session) throw new Error('Nie utworzono sesji.');
      const clean = new URL(window.location.href); clean.searchParams.delete('code');
      window.history.replaceState(window.history.state, '', clean.pathname + clean.search + clean.hash);
      return exchanged.data.session;
    }
    const current = await supabase.auth.getSession();
    if (current.error) throw current.error;
    if (current.data.session) return current.data.session;
    throw new Error('Brak kodu autoryzacyjnego. Rozpocznij logowanie ponownie.');
  };
  authCallbackPromise = callback();
  try {
    return await authCallbackPromise;
  } catch (error) {
    authCallbackPromise = null;
    throw error;
  }
}

export async function currentSession(): Promise<Session | null> {
  if (!supabase) return null;
  return (await supabase.auth.getSession()).data.session;
}

export async function signOut() {
  if (!supabase) return;
  await supabase.auth.signOut({ scope: 'local' });
  authCallbackPromise = null;
}
